import {
  closesWithTurn,
  type PlanStep,
  type WorkPlan,
} from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import type { AnsweredTurnView, RepReplyView } from "./rep-reply.ts";

/*
 * What the compose box and the Work column beside it show, decided here so the page only renders
 * it and the tests can hold it (phase 1 of docs/2026-09-23-compose-view-shape.md, "States",
 * "Keyboard"). Nothing here sends: the box's way out is copy, then the rep's word that it went.
 */

export type ComposeAction = {
  id: "discard" | "copy" | "open_bison" | "report";
  label: string;
  tone: "primary" | "plain" | "quiet";
  /** The single-letter key that presses it. Only copying has one; nothing that records does. */
  key: "C" | null;
  enabled: boolean;
  /** Why it is off, said beside it rather than left for the rep to guess. */
  reason: string | null;
};

export type ComposeNotice = {
  tone: "warn" | "info";
  text: string;
  /** The one control a notice may carry: moving the reply to the lead's newest message. */
  action: "answer_newest" | null;
};

type Formatter = (at: string) => string;

/** Notices above the editor, most consequential first. */
export function composeNotices(
  view: RepReplyView,
  when: Formatter,
  newest: { at: string } | null,
): ComposeNotice[] {
  const notices: ComposeNotice[] = [];
  if (view.suppressed)
    notices.push({
      tone: "warn",
      text: "This lead asked not to be contacted. Reply only if you must, for example to confirm the removal.",
      action: null,
    });
  const reply = view.reply;
  if (reply && !reply.turnCurrent)
    notices.push({
      tone: "warn",
      text: `This answers an earlier message.${newest ? ` The lead wrote again ${when(newest.at)}.` : ""}`,
      action: "answer_newest",
    });
  if (view.pipelineSend?.state === "in_flight")
    notices.push({
      tone: "warn",
      text: `Draft r${view.pipelineSend.draftRevision} is being sent now. Wait for it before you reply yourself.`,
      action: null,
    });
  if (view.pipelineSend?.state === "uncertain")
    notices.push({
      tone: "warn",
      text: `Draft r${view.pipelineSend.draftRevision} may already have gone out. Check Bison first.`,
      action: null,
    });
  if (view.header.kind === "unresolvable")
    notices.push({
      tone: "info",
      text: "Can't tell who this goes to. Write it here, then answer the lead from Bison.",
      action: null,
    });
  if (view.answered && view.answered.author !== "rep")
    notices.push({
      tone: "info",
      text:
        view.answered.author === "outside_app"
          ? "Someone already answered this message in Bison. Yours would be a second reply."
          : "The pipeline's draft was already reported sent. Yours would be a second reply.",
      action: null,
    });
  return notices;
}

/**
 * The action row. While writing: discard, then copy. Once copied: copy again, open Bison, and the
 * rep's word that it went. No action here sends, and none is labelled as if it did.
 */
export function composeActions(
  view: RepReplyView,
  page: { hasText: boolean; saveFailed: boolean; bisonUrl: boolean },
): ComposeAction[] {
  const phase = view.reply?.phase.kind ?? "writing";
  const inFlight = view.pipelineSend?.state === "in_flight";
  const blocked = inFlight
    ? `Draft r${view.pipelineSend?.draftRevision ?? 0} is being sent now.`
    : null;
  const copyReason = blocked
    ? blocked
    : page.saveFailed
      ? "Couldn't save. Your text is still here."
      : !page.hasText
        ? "Write your reply first."
        : null;
  const copy: ComposeAction = {
    id: "copy",
    label: phase === "copied" ? "Copy again" : "Copy reply",
    tone: phase === "copied" ? "quiet" : "primary",
    key: "C",
    enabled: copyReason === null,
    reason: copyReason,
  };
  if (phase !== "copied")
    return [
      {
        id: "discard",
        label: "Discard",
        tone: "quiet",
        key: null,
        enabled: true,
        reason: null,
      },
      copy,
    ];
  const reportReason =
    blocked ??
    (view.turn === null
      ? "There is no message from the lead to answer."
      : view.reply && !view.reply.turnCurrent
        ? "Move it to the lead's newest message first."
        : null);
  return [
    copy,
    ...(page.bisonUrl
      ? [
          {
            id: "open_bison" as const,
            label: "Open Bison",
            tone: "plain" as const,
            key: null,
            enabled: true,
            reason: null,
          },
        ]
      : []),
    {
      id: "report",
      label: "I sent it in Bison",
      tone: "primary",
      key: null,
      enabled: reportReason === null,
      reason: reportReason,
    },
  ];
}

export type WorkFold =
  /** Nothing folded: the Work column shows every step as before. */
  | { kind: "open" }
  /** The rep is writing: the steps a reply would close fold into one line. */
  | { kind: "paused"; stepIds: string[]; summary: string }
  /** A seller reply answered the turn: what it closed folds, kept and not deleted. */
  | {
      kind: "answered";
      stepIds: string[];
      summary: string;
      author: AnsweredTurnView["author"];
      at: string;
      seenAt: string | null;
    };

/** "2 questions · draft r3", from the steps themselves. */
function describeSteps(steps: readonly PlanStep[]): string {
  const questions = steps.filter((step) => step.input.kind === "question").length;
  const drafts = steps.flatMap((step) =>
    step.input.kind === "reply" ? [`draft r${step.input.draftRevision}`] : [],
  );
  return [
    ...(questions ? [`${questions} ${questions === 1 ? "question" : "questions"}`] : []),
    ...drafts,
  ].join(" · ");
}

/**
 * What the Work column folds. While the box is open, exactly the steps `settleTurn` would close,
 * so the line says what the record will do. Once the turn is answered, the steps the record closed
 * plus a draft that arrived after it, so the rep can still open them.
 */
export function workFold(
  plan: WorkPlan,
  answered: AnsweredTurnView | null,
  writing: boolean,
  lateDrafts: ReadonlySet<number>,
): WorkFold {
  if (answered) {
    const settled = new Set(answered.settledStepIds);
    const steps = plan.steps.filter(
      (step) =>
        settled.has(step.id) ||
        (step.input.kind === "reply" &&
          step.state === "skipped" &&
          lateDrafts.has(step.input.draftRevision)),
    );
    if (steps.length || answered.author === "rep")
      return {
        kind: "answered",
        stepIds: steps.map((step) => step.id),
        summary: describeSteps(steps),
        author: answered.author,
        at: answered.at,
        seenAt: answered.seenAt,
      };
  }
  if (!writing) return { kind: "open" };
  const steps = plan.steps.filter(closesWithTurn);
  return steps.length
    ? { kind: "paused", stepIds: steps.map((step) => step.id), summary: describeSteps(steps) }
    : { kind: "open" };
}

/** The badge a folded step carries once it is shown: closed by whom, never just "Skipped". */
export function closedStepLabel(
  step: PlanStep,
  answered: AnsweredTurnView,
  lateDrafts: ReadonlySet<number>,
): string | null {
  if (step.input.kind === "reply" && lateDrafts.has(step.input.draftRevision))
    return "Arrived after your reply";
  if (!answered.settledStepIds.includes(step.id)) return null;
  if (answered.author === "rep")
    return step.input.kind === "reply" ? "Not sent: you replied yourself" : "Closed by your reply";
  return step.input.kind === "reply"
    ? "Not sent: answered in Bison"
    : "Closed by the reply in Bison";
}

/**
 * Pipeline drafts written after the turn was answered (R5 keeps them, projected closed). Their
 * revision time is the draft row's; anything written before the answer is the draft it closed.
 */
export function lateDraftRevisions(
  drafts: readonly { revision: number; createdAt: string }[],
  answered: AnsweredTurnView | null,
): Set<number> {
  if (!answered) return new Set();
  const at = Date.parse(answered.at);
  return new Set(
    drafts.filter((draft) => pgInstant(draft.createdAt) > at).map((draft) => draft.revision),
  );
}

/** A Postgres `timestamptz::text` ("2026-09-08 12:00:00.1+00") as epoch milliseconds, in any engine. */
function pgInstant(text: string): number {
  return Date.parse(text.replace(" ", "T").replace(/([+-]\d\d)$/u, "$1:00"));
}
